
<template>
  <block-item-card
    :min-height="minBlockHeight"
    :title="$t('ALM.Section.GapAnalysis.Title')"
    :title-info="$t('ALM.Section.GapAnalysis.TitleInfo')"
  >
    <v-row class="ma-0">
      <v-col
        class="ma-0 pa-2"
        cols="12"
      >
        <!-- Filters and Toggle -->
        <div class="d-flex justify-space-between align-center px-2">
          <!-- Filter Buttons -->
          <div class="d-flex gap-2">
            <asset-button-group
              :disabled="!almSummary"
              :height="26"
              :items="gapFilterItems"
              :selected.sync="selectedFilter"
              disable-dropdown-on-mobile
            />
          </div>

          <!-- Rate Type Toggle -->
          <div class="mr-4">
            <banking-switch
              v-model="floatingRate"
              :enabled-label="$t('ALM.Section.GapAnalysis.FloatingRate')"
              :disabled-label="$t('ALM.Section.GapAnalysis.FixedRate')"
            />
          </div>
        </div>

        <div class="gap-chart-container">
          <v-lazy
            :options="{threshold: .5}"
            transition="fade-transition"
          >
            <!-- Regular Bar Chart for Total, Loans, Deposits, Investments -->
            <base-e-chart-bar
              v-if="selectedFilter !== 'All 3'"
              :chart-data-options="gapChartData"
              :chart-grid-margin="{left: 0, top: 50, right: 0, bottom: 0, containLabel: true}"
              :axis-y-label-show="false"
              :axis-y-split-line-show="true"
              :axis-y-split-line-type="[8,8]"
              :axis-x-label-show="true"
              chart-animation-easing="elasticOut"
              :bar-width="'40%'"
              axis-y-label-value-format="number"
              axis-x-label-value-format="number"
              :show-series-label="true"
              :axis-x-tick-prefix="currencySymbol"
              :silent="false"
              :bar-color="gapBarColors"
              :tooltip-formatter="gapBarTooltipFormatter"
            />

            <!-- Grouped Bar Chart for All 3 -->
            <gap-dataset-bar
              v-else
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import BaseEChartBar from '@/theme/default/components/charts/echarts/BaseEChartBar'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'
import GapDatasetBar from '@/theme/default/views/banking/alm/summary/charts/GapDatasetBar.vue'

export default {
  name      : 'GapAnalysis',
  components: {
    BlockItemCard,
    BaseEChartBar,
    AssetButtonGroup,
    BankingSwitch,
    GapDatasetBar
  },
  mixins   : [chartsColorMixin],
  props    : {},
  dataStore: {
    ALMSummary: 'ALM.ALMSummary.Data'
  },
  data () {
    return {
      selectedFilter: 'Total',
      floatingRate  : true,
      minBlockHeight: 400,
      gapFilters    : [
        { label: this.$t('ALM.Section.GapAnalysis.NetExposure'), value: 'Total' },
        { label: this.$t('ALM.Section.GapAnalysis.GrossExposure'), value: 'All 3' },
        { label: this.$t('ALM.Section.GapAnalysis.Loans'), value: 'Loans', color: '#57C05B' },
        { label: this.$t('ALM.Section.GapAnalysis.Deposits'), value: 'Deposits', color: '#3D68D5' },
        { label: this.$t('ALM.Section.GapAnalysis.Investments'), value: 'Investments', color: '#EA804B' }
      ]
    }
  },
  computed: {
    almSummary () {
      return this.ALMSummary || {}
    },

    currencySymbol () {
      return this.almSummary?.currencySymbol || ''
    },

    gapFilterItems () {
      return this.gapFilters.map(filter => ({
        Title: filter.label,
        Value: filter.value,
        icon : filter.color ? { color: filter.color } : null
      }))
    },

    gapBarLabels () {
      return this.almSummary?.gapAnalysisLabels || []
    },

    gapBarData () {
      return this.almSummary?.gapAnalysisBarData || []
    },

    // display total data
    gapChartData () {
      const labels = this.gapBarData.map(item => item.label)
      const values = this.gapBarData.map(item => item.value)

      return [{
        x: labels,
        y: values
      }]
    },

    gapBarColors () {
      switch (this.selectedFilter) {
        case 'Loans':
          return this.themeColorRGBA('synthesis-ui-green-02')
        case 'Investments':
          return this.themeColorRGBA('synthesis-ui-orange-02')
        case 'Deposits':
          return this.themeColorRGBA('synthesis-ui-blue-04')
        default:
          return this.gapBarData.map(item => item.value > 0 ? this.themeColorRGBA('synthesis-ui-purple-03') : this.themeColorRGBA('synthesis-ui-red-03'))
      }
    },

    selectedRateType () {
      return this.floatingRate ? 'floating' : 'fixed'
    },

    gapBarTooltipFormatter () {
      return (params) => {
        return `<div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${params.color}; margin-right: 8px; border-radius: 2px;"></span>
                <span style="font-weight: 500;">${params.name}:</span>
                <span style="margin-left: 8px; font-weight: bold;">${this.currencySymbol}${(params.value === 0 ? '0' : toNumberFormatNotationShort(params.value))}</span>
              </div>`
      }
    }
  },
  watch: {
    almSummary: {
      immediate: true,
      handler (newAlmSummary) {
        if (newAlmSummary) {
          newAlmSummary.selectedGapFilter = this.selectedFilter
          newAlmSummary.selectedRateType = this.selectedRateType
        }
      }
    },
    selectedFilter (newFilter) {
      if (this.almSummary) {
        this.almSummary.selectedGapFilter = newFilter
      }
    },
    selectedRateType (newRateType) {
      if (this.almSummary) {
        this.almSummary.selectedRateType = newRateType
      }
    }
  }
}
</script>

<style scoped>
.gap-chart-container {
  height: 300px;
  width: 100%;
}
</style>
